import { Link } from 'react-router-dom';
import { Package, RotateCcw, ChevronRight } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PwaTrialBanner } from './PwaTrialBanner';

/**
 * Hero-style landing. Two big tiles that dominate the screen — the
 * primary workflows the operator picks between. Above them a quiet
 * personal greeting (iOS-style large title); no other chrome.
 */
export function PwaHomePage() {
  const me = useCurrentUser();
  const firstName = friendlyFirstName(me?.fullName);

  return (
    <div className="pwa-anim-in flex h-full flex-col gap-4 px-4 pb-6 pt-4">
      <PwaTrialBanner />
      <div className="space-y-1">
        <h1 className="text-[26px] font-bold leading-tight tracking-tight text-foreground">
          {greeting()}
          {firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          Pick a workflow to get started.
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-3">
        <HeroTile
          to="/pwa/pack"
          icon={<Package className="h-8 w-8" />}
          title="Pack dispatch"
          subtitle="Scan SKU + security seal as you pack each unit."
          tone="primary"
        />
        <HeroTile
          to="/pwa/returns"
          icon={<RotateCcw className="h-8 w-8" />}
          title="Receive return"
          subtitle="Scan seal at intake. Broken seals block the return."
          tone="accent"
        />
      </div>
    </div>
  );
}

function HeroTile({
  to,
  icon,
  title,
  subtitle,
  tone,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  tone: 'primary' | 'accent';
}) {
  const surfaceClass =
    tone === 'primary'
      ? 'bg-gradient-to-br from-primary/15 to-primary/5 border-primary/30'
      : 'bg-gradient-to-br from-accent/15 to-accent/5 border-accent/30';
  const iconWrap =
    tone === 'primary'
      ? 'bg-primary text-primary-foreground'
      : 'bg-accent text-accent-foreground';
  return (
    <Link
      to={to}
      className={`pwa-tap relative flex flex-1 items-center gap-4 overflow-hidden rounded-2xl border ${surfaceClass} px-5 py-6 shadow-elev-1 active:shadow-none`}
    >
      <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-xl ${iconWrap} shadow-sm`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="text-[17px] font-semibold leading-tight tracking-tight text-foreground">
          {title}
        </div>
        <p className="text-[12.5px] leading-snug text-muted-foreground">
          {subtitle}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-foreground/40" />
    </Link>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Hello';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
}

/**
 * Pick the user's friendly first name out of `fullName`. The tenant's
 * data sometimes prefixes the given name with an initial (e.g.
 * "H Kushal Bansal" or "H. Kushal" or "H, Kushal"), and naively
 * taking `split(' ')[0]` then renders the greeting as "Good morning, H"
 * — wrong, both for the bare "H" and for the fact that the comma
 * splits visually. Filter out single-character tokens and pick the
 * first real word; falls back to whatever's there if there's no
 * multi-char token (very short names).
 */
function friendlyFirstName(fullName: string | null | undefined): string {
  if (!fullName) return '';
  const tokens = fullName
    .trim()
    .split(/[\s,.]+/)
    .filter(Boolean);
  if (tokens.length === 0) return '';
  const firstReal = tokens.find((t) => t.length > 1);
  return firstReal ?? tokens[0];
}
